import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accessibility, Loader2, ClipboardList, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useA11y } from "@/context/A11yContext";
import { isTenantAdmin } from "@/lib/roles";

function Row({ label, hint, children, htmlFor }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="min-w-0">
        <Label htmlFor={htmlFor} className="text-xs text-[color:var(--ink-2)]">{label}</Label>
        {hint && <div className="text-[10px] text-[color:var(--ink-3)]">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

// Item 19: display preferences (per user) + conformance policy (per tenant, admin).
export function AccessibilityCard() {
  const { user } = useAuth();
  const { prefs, setPrefs } = useA11y();
  const admin = isTenantAdmin(user?.role);
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState("");
  useEffect(() => { if (admin) api.get("/a11y/tenant").then((r) => setPolicy(r.data.policy)).catch(() => setPolicy({})); }, [admin]);

  const savePref = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next); // instant feedback
    try { const { data } = await api.put("/a11y/me", patch); setPrefs(data.prefs); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save preference"); }
  };
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const runReport = async () => {
    setLoadingReport(true);
    try { const { data } = await api.get("/a11y/compliance"); setReport(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not build the compliance report"); }
    finally { setLoadingReport(false); }
  };
  const exportReport = () => {
    if (!report) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      ["Accessibility compliance report", report.generated_at, `level ${report.policy?.level || "AA"}`].map(esc).join(","),
      "",
      ["Dashboard", "Status", "Owner", "Score", "Errors", "Warnings", "Info", "Widgets"].map(esc).join(","),
      ...report.dashboards.map((d) => [d.name, d.status, d.owner_email, d.score, d.errors, d.warnings, d.info, d.widgets].map(esc).join(",")),
      "",
      ["Rule", "Severity", "WCAG", "Occurrences", "Dashboards", "How to fix"].map(esc).join(","),
      ...report.rules.map((r) => [r.rule, r.severity, r.wcag, r.count, r.dashboards, r.fix].map(esc).join(",")),
    ];
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = `accessibility-compliance-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const savePolicy = async () => {
    setSaving("policy");
    try { const { data } = await api.put("/a11y/tenant", policy); setPolicy(data.policy); toast.success("Accessibility policy saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save policy"); } finally { setSaving(""); }
  };
  const sel = (k, opts, testid) => (
    <Select value={prefs[k]} onValueChange={(v) => savePref({ [k]: v })}>
      <SelectTrigger id={testid} data-testid={testid} className="h-8 text-xs bg-[color:var(--surface-2)] w-[190px]"><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <Card data-testid="a11y-card" className="glass-card border-0 p-6">
      <div className="flex items-center gap-2">
        <Accessibility className="w-5 h-5 text-[color:var(--brand-ocean)]" aria-hidden="true" />
        <h3 className="font-display text-lg text-[color:var(--ink)]">Accessibility</h3>
      </div>
      <p className="text-xs text-[color:var(--ink-3)] mt-1">WCAG 2.2 AA · Section 508. Display preferences apply immediately on this account across every dashboard.</p>
      <div className="mt-3 divide-y divide-[color:var(--glass-border)]">
        <Row label="Contrast" hint="High contrast darkens secondary text and borders" htmlFor="a11y-contrast">{sel("contrast", [["auto", "Follow system"], ["normal", "Normal"], ["high", "High contrast"]], "a11y-contrast")}</Row>
        <Row label="Motion" hint="Reduce disables transitions and chart animations" htmlFor="a11y-motion">{sel("motion", [["auto", "Follow system"], ["reduce", "Reduce motion"], ["allow", "Allow motion"]], "a11y-motion")}</Row>
        <Row label="Text size" htmlFor="a11y-text">{sel("text", [["normal", "Normal"], ["large", "Large (112.5%)"]], "a11y-text")}</Row>
        <Row label="Show charts as data tables" hint="Adds a visible 'Table' toggle on every chart (screen readers always get one)">
          <Switch data-testid="a11y-chart-tables" checked={!!prefs.chart_tables} onCheckedChange={(v) => savePref({ chart_tables: v })} aria-label="Show charts as data tables" />
        </Row>
        <Row label="Pattern fills on charts" hint="Distinguish series without relying on colour">
          <Switch data-testid="a11y-chart-patterns" checked={!!prefs.chart_patterns} onCheckedChange={(v) => savePref({ chart_patterns: v })} aria-label="Pattern fills on charts" />
        </Row>
        <Row label="Always show focus outline">
          <Switch data-testid="a11y-focus-ring" checked={prefs.focus_ring !== false} onCheckedChange={(v) => savePref({ focus_ring: v })} aria-label="Always show focus outline" />
        </Row>
      </div>

      {admin && (
        <div className="mt-5 pt-4 border-t border-[color:var(--glass-border)]">
          <div className="text-sm font-medium text-[color:var(--ink)]">Tenant conformance policy <span className="text-[10px] text-[color:var(--ink-3)] font-normal">(admin)</span></div>
          {policy === null ? <Loader2 className="w-4 h-4 animate-spin mt-2 text-[color:var(--ink-3)]" /> : (
            <div className="mt-2 divide-y divide-[color:var(--glass-border)]">
              <Row label="Conformance level" htmlFor="a11y-policy-level">
                <Select value={policy.level || "AA"} onValueChange={(v) => setPolicy({ ...policy, level: v })}>
                  <SelectTrigger id="a11y-policy-level" data-testid="a11y-policy-level" className="h-8 text-xs bg-[color:var(--surface-2)] w-[190px]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="AA">WCAG 2.2 AA (4.5:1)</SelectItem><SelectItem value="AAA">WCAG 2.2 AAA (7:1)</SelectItem></SelectContent>
                </Select>
              </Row>
              <Row label="Block publishing on errors" hint="Dashboards with accessibility errors cannot be published">
                <Switch data-testid="a11y-policy-enforce" checked={!!policy.enforce_on_publish} onCheckedChange={(v) => setPolicy({ ...policy, enforce_on_publish: v })} aria-label="Block publishing on accessibility errors" />
              </Row>
              <Row label="Require widget titles"><Switch data-testid="a11y-policy-titles" checked={policy.require_widget_titles !== false} onCheckedChange={(v) => setPolicy({ ...policy, require_widget_titles: v })} aria-label="Require widget titles" /></Row>
              <Row label="Require image alt text"><Switch data-testid="a11y-policy-alt" checked={policy.require_image_alt !== false} onCheckedChange={(v) => setPolicy({ ...policy, require_image_alt: v })} aria-label="Require image alt text" /></Row>
              <Row label="Flag colour-only formatting"><Switch data-testid="a11y-policy-color" checked={policy.forbid_color_only !== false} onCheckedChange={(v) => setPolicy({ ...policy, forbid_color_only: v })} aria-label="Flag colour-only conditional formatting" /></Row>
              <Row label="Minimum font size (px)" htmlFor="a11y-policy-font">
                <Input id="a11y-policy-font" data-testid="a11y-policy-font" type="number" min={8} max={24} value={policy.min_font_px ?? 11} onChange={(e) => setPolicy({ ...policy, min_font_px: Number(e.target.value) })} className="h-8 w-20 text-xs bg-[color:var(--surface-2)]" />
              </Row>
              <div className="pt-3">
                <Button data-testid="a11y-policy-save" size="sm" onClick={savePolicy} disabled={saving === "policy"} className="bg-[color:var(--brand-ocean)] text-white">{saving === "policy" && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save policy</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {admin && (
        <div className="mt-5 pt-4 border-t border-[color:var(--glass-border)]">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium text-[color:var(--ink)] flex items-center gap-1.5"><ClipboardList className="w-4 h-4 text-[color:var(--brand-ocean)]" aria-hidden="true" />Compliance report</div>
              <p className="text-[10px] text-[color:var(--ink-3)]">Audits every dashboard in this workspace against the policy above.</p>
            </div>
            <div className="flex items-center gap-1.5">
              {report && <Button data-testid="a11y-report-export" size="sm" variant="outline" onClick={exportReport} className="h-8 text-xs"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>}
              <Button data-testid="a11y-report-run" size="sm" onClick={runReport} disabled={loadingReport} className="h-8 text-xs bg-[color:var(--brand-ocean)] text-white">
                {loadingReport && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}{report ? "Refresh" : "Run report"}
              </Button>
            </div>
          </div>
          {report && (
            <div data-testid="a11y-report" className="mt-3 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[["Dashboards", report.totals.dashboards], ["Pass rate", `${report.totals.pass_rate}%`], ["Avg score", report.totals.avg_score], ["Errors", report.totals.errors]].map(([l, v], i) => (
                  <div key={l} data-testid={`a11y-report-stat-${i}`} className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">{l}</div>
                    <div className="text-lg font-display text-[color:var(--ink)]">{v}</div>
                  </div>
                ))}
              </div>
              {report.rules.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-[color:var(--ink-2)] mb-1">Most common violations</div>
                  <div className="rounded-xl border border-[color:var(--glass-border)] overflow-hidden">
                    <table className="w-full text-[11px]">
                      <thead className="bg-[color:var(--surface-2)] text-[color:var(--ink-3)]"><tr><th scope="col" className="text-left px-2 py-1 font-medium">Rule</th><th scope="col" className="text-left px-2 py-1 font-medium">WCAG</th><th scope="col" className="text-right px-2 py-1 font-medium">Issues</th><th scope="col" className="text-right px-2 py-1 font-medium">Dashboards</th></tr></thead>
                      <tbody data-testid="a11y-report-rules">
                        {report.rules.slice(0, 8).map((r) => (
                          <tr key={r.rule} className="border-t border-[color:var(--glass-border)]">
                            <td className="px-2 py-1 text-[color:var(--ink-2)]"><span className={"inline-block w-1.5 h-1.5 rounded-full mr-1.5 " + (r.severity === "error" ? "bg-[color:var(--danger)]" : r.severity === "warning" ? "bg-amber-500" : "bg-[color:var(--ink-3)]")} />{r.rule}</td>
                            <td className="px-2 py-1 text-[color:var(--ink-3)]">{r.wcag || "—"}</td>
                            <td className="px-2 py-1 text-right tabular-nums text-[color:var(--ink-2)]">{r.count}</td>
                            <td className="px-2 py-1 text-right tabular-nums text-[color:var(--ink-3)]">{r.dashboards}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-medium text-[color:var(--ink-2)] mb-1">Needs attention</div>
                <div className="rounded-xl border border-[color:var(--glass-border)] divide-y divide-[color:var(--glass-border)]" data-testid="a11y-report-dashboards">
                  {report.dashboards.slice(0, 6).map((d) => (
                    <div key={d.id} className="flex items-center gap-2 px-2 py-1.5">
                      <span className={"text-[11px] tabular-nums w-8 text-right " + (d.score >= 90 ? "text-[color:var(--brand-mint)]" : d.score >= 70 ? "text-amber-600" : "text-[color:var(--danger)]")}>{d.score}</span>
                      <span className="text-[11px] text-[color:var(--ink-2)] truncate flex-1">{d.name}</span>
                      <span className="text-[10px] text-[color:var(--ink-3)]">{d.errors} err · {d.warnings} warn</span>
                      <a data-testid={`a11y-report-open-${d.id}`} href={`/builder/${d.id}`} className="text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]" aria-label={`Open ${d.name}`}><ExternalLink className="w-3.5 h-3.5" /></a>
                    </div>
                  ))}
                  {!report.dashboards.length && <p className="px-2 py-2 text-[11px] text-[color:var(--ink-3)]">No dashboards to audit yet.</p>}
                </div>
              </div>
              <p className="text-[10px] text-[color:var(--ink-3)]">Generated {new Date(report.generated_at).toLocaleString()} · level {report.policy?.level || "AA"}{report.truncated ? " · first 400 dashboards" : ""}</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
